
import NotificationService from '@/libs/core/notifications/notification-service';
import { Component, Vue, Watch, Ref } from 'vue-property-decorator';
import { DropDownButtonPlugin } from '@syncfusion/ej2-vue-splitbuttons';

import { authStore } from '@/libs/auth/+store/store';
import { tenantsStore, tenantUsersStore } from '@/libs/tenants/+state/store';
import { AuthAction, AuthGetter } from '@/libs/auth/models/auth-state';
import { CrudAction, CrudReponse, CrudResponseAction } from './libs/core/+state/models/crud-action';
import { createCrudQueryPayload } from './libs/core/+state/models/crud-query-payload';
import {
    ApplicationUserModel,
    CodebookModel,
    CustomerModel,
    GroupModel,
    LocationModel,
    PermissionTypeEnum,
    ProductModel,
    ProjectModel,
    RoleModel,
    ScrapReasonModel,
    ScrapReasonWorkplaceModel,
    ShiftTypeModel,
    StationModel,
    TenantModel,
    TenantUserModel,
    WorkplaceModel,
} from './libs/Api';
import { CrudGetter } from './libs/core/+state/models/crud-getter';
import { EventTypeEnum } from '@/libs/core/models/event-type-enum';

import { formatDate } from '@/libs/common/functions/format-date';
import { applicationUserStore } from './libs/user-settings/+state/store';
import Page from './Page.vue';
import { codebooksStore } from './libs/codebooks/+state/store';

import Sidebar from '@/libs/common/components/Sidebar.vue';

import { GlobalVariables } from '@/libs/core/global';
import { workplacesStore } from './libs/workplaces/+state/store';
import { rolesStore } from './libs/roles/+state/store';
import { groupsStore } from './libs/groups/+state/store';
import moment from 'moment';
import { AuthenticationType } from './libs/auth/models/auth-type';
import { PublicClientApplication } from '@azure/msal-browser';
import { shiftTypesStore } from '@/libs/shift-types/+state/store';
import { customersStore } from '@/libs/customers/+state/store';
import { locationsStore } from '@/libs/locations/+state/store';
import VuePdfEmbed from 'vue-pdf-embed/dist/vue2-pdf-embed';
import Numpad from '@/libs/common/components/Numpad.vue';
import QuickLoginDialog from '@/libs/common/components-smart/QuickLoginDialog.vue';
import ActivateQuickLoginDialog from '@/libs/common/components-smart/ActivateQuickLoginDialog.vue';
import DeactivateQuickLoginDialog from '@/libs/common/components-smart/DeactivateQuickLoginDialog.vue';
import MyPermissionsDialog from '@/libs/user-settings/components/MyPermissionsDialog.vue';
import { productsStore } from '@/libs/products/+state/store';
import { projectsStore } from '@/libs/projects/+state/store';
import { stationsStore } from '@/libs/stations/+state/store';
import NotificationSubscription from '@/libs/common/components-smart/NotificationSubscription.vue';
import webPushNotifications from '@/libs/core/web-push-notifications';
import { notificationsStore } from '@/libs/notifications/+state/store';
import TakeSolvingItem from '@/libs/issues-solving/components/TakeSolvingItem.vue';
import { scrapReasonsStore, scrapReasonWorkplacesStore } from '@/libs/scrap-reasons/+state/store';
import ServiceQueueDialog from '@/libs/common/components-smart/ServiceQueueDialog.vue';

Vue.use(DropDownButtonPlugin);

@Component({
    components: {
        TakeSolvingItem,
        NotificationSubscription,
        DeactivateQuickLoginDialog,
        ActivateQuickLoginDialog,
        QuickLoginDialog,
        MyPermissionsDialog,
        Numpad,
        VuePdfEmbed,
        Sidebar,
        ServiceQueueDialog,
    },
})
export default class App extends Page {
    loading = true;
    user: any = {};
    selectedTenant: string | null = null;
    locale = 'cs';
    tenantMenu = false;
    pageTitle: string | undefined | null = null;
    showMobileHeader = true;
    activateQuickLoginDialog = false;
    deactivateQuickLoginDialog = false;
    quickLoginDialog = false;
    myPermissionsDialog = false;
    hasAccessToRoute = true;
    quickLoginTimeout = 0;
    quickLoginPassTime = 0;
    quickLoginRemainingSeconds = 0;
    quickLoginRemainingCriticalSeconds = 30;
    isQuickLoginLocked = false;
    refreshInterval: any = null;
    signoutDialog = false;
    isLocked = false;
    takeItemId: string | null | undefined = null;
    serviceQueueDialog = false;
    previousScrollTop = -1;

    @Ref() notificationSubscription!: NotificationSubscription;

    //@Prop() tenantId!: string;

    get tenantId(): string | null {
        return authStore.useGetter(AuthGetter.GetTenant);
    }

    get isLoggedIn(): boolean {
        return authStore.useGetter(AuthGetter.GetIsLoggedIn);
    }

    get tenants(): Array<TenantModel> {
        return (tenantsStore.useGetter(CrudGetter.Data) as TenantModel[]).orderBy((x) => x.name).toArray();
    }

    get tenant(): TenantModel {
        return tenantsStore.useGetter(CrudGetter.Detail);
    }

    get userInfo(): ApplicationUserModel {
        return applicationUserStore.useGetter(CrudGetter.Detail);
    }

    get isSharedMode(): boolean {
        return authStore.useGetter(AuthGetter.GetSharedMode);
    }

    get isNotificationMode(): boolean {
        return authStore.useGetter(AuthGetter.GetNotificationMode);
    }

    get isTvMode(): boolean {
        return authStore.useGetter(AuthGetter.GetTvMode);
    }

    get remainingSecondsString(): string {
        if (this.quickLoginRemainingSeconds > 0) {
            const minutes = Math.floor(this.quickLoginRemainingSeconds / 60);
            const seconds = this.quickLoginRemainingSeconds - minutes * 60;
            return `${minutes.toFixed(0).padStart(2, '0')}:${seconds.toFixed(0).padStart(2, '0')}`;
        }
        return '00:00';
    }

    get useTpm(): boolean {
        return (window as any).UseTpm ?? false;
    }

    @Watch('tenant.isRequiredTwoFactor')
    isRequiredTwoFactorChanged() {
        this.detectTwoFactorRequirements();
    }

    created() {
        this.$i18n.locale = window.localStorage.language ?? 'cs';
        this.locale = this.$i18n.locale;
        this.$vuetify.lang.current = this.locale;
        GlobalVariables.lang = this.locale;
        moment.locale(this.$vuetify.lang.current);
    }

    @Watch('isLoggedIn')
    isLoggedInChanged(v: boolean) {
        if (!this.loading) {
            if (!v) {
                if (
                    this.$route.name != 'PasswordRecovery' &&
                    this.$route.name != 'SignIn' &&
                    this.$route.name != 'SignUp' &&
                    this.$route.name != 'SignUpOrganization'
                )
                    this.$router.push({ name: 'SignIn' });
            } else {
                this.connectSignalR();
                //this.loadUserData();
            }
        }
    }

    @Watch('tenantId')
    tenantIdChanged(v: string, p: string) {
        this.selectedTenant = v;
        //this.loading = true;
        //this.loadTenantStores();
        if (v) {
            NotificationService.joinTenant(v);
        }
        if (p != null && p != '' && v != null && v != '' && v != p) {
            const currentRoute = { ...this.$router.currentRoute };
            currentRoute.params.tenantId = v;
            this.$router.replace({
                name: currentRoute.name!,
                params: currentRoute.params!,
                query: currentRoute.query!,
            });
            if (p != null) {
                const keys = [
                    'wtView',
                    'wtParty',
                    'wtDistrict',
                    'wtTown',
                    'wtUser',
                    'wtCategory',
                    'wtDepartment',
                    'wtPosition',
                    'wtPageSize',
                    'ptPageSize',
                ];
                for (const key of keys) {
                    window.localStorage.removeItem(key);
                }
                window.location.reload();
            }
        }
    }

    @Watch('selectedTenant')
    selectedTenantChanged(v: string, p: string) {
        if (p != null && p != '' && v != null && v != '' && v != this.$route.params.tenantId) {
            authStore.dispatch(AuthAction.SetTenant, v);
        }
    }

    @Watch('$route')
    appRouteChanged(route: any, prevRoute: any) {
        if (!this.loading) {
            if (prevRoute?.name == 'PanelDashboard' && route?.name != 'PanelDashboard') {
                const user = applicationUserStore.useGetter(CrudGetter.Detail) as ApplicationUserModel;
                const language = user.language ?? 'cs';
                this.$i18n.locale = language;
                window.localStorage.language = language;
                this.$vuetify.lang.current = language;
                GlobalVariables.lang = language;
                moment.locale(this.$vuetify.lang.current);
            }
            this.detectTenantMenu();
            this.detectPermissions();
            this.detectSystemUser();
            this.detectLicence();
            this.detectTwoFactorRequirements();
            this.getPageTitle();
            this.setDocumentTitle();
            this.isMobileHeaderShown();
            if (this.tenantMenu && this.tenantId == null) {
                const tenants = tenantsStore.useGetter(CrudGetter.Data) as TenantModel[];
                if (tenants.length == 0) {
                    this.$router.push({ name: 'UserSettings' });
                } else {
                    authStore.dispatch(AuthAction.SetTenant, tenants[0].id!);
                }
            }
        }
    }

    async mounted() {
        this.detectTenantMenu();
        this.getPageTitle();
        this.setDocumentTitle();
        this.isMobileHeaderShown();
        const envKey = process.env.NODE_ENV != 'production' ? '.'.concat(process.env.NODE_ENV!) : '';
        fetch(process.env.BASE_URL + 'config'.concat(envKey).concat('.json')).then((result) => {
            result.json().then((jsonBody: any) => {
                (window as any).ApiService.baseUrl = jsonBody.ApiEndpoint;
                (window as any).SaasApiEndpoint = jsonBody.SaasApiEndpoint;
                (window as any).NotificationVersion = jsonBody.NotificationVersion;
                (window as any).UseTpm = jsonBody.UseTpm ?? false;
                (window as any).EnableAccountCreate = jsonBody.EnableAccountCreate ?? true;
                (window as any).BlobStorage = jsonBody.BlobStorage ?? undefined;
                (window as any).UseAms = jsonBody.UseAms ?? false;
                (window as any).RestrictTenantCreation = jsonBody.RestrictTenantCreation ?? false;
                webPushNotifications.registerServiceWorker();
                notificationsStore.dispatch(
                    CrudAction.GetAll,
                    createCrudQueryPayload(undefined, undefined, { pageIndex: 1, pageSize: 1 })
                );
                const supportedAuthenticationTypes = jsonBody.Authentication;
                authStore.dispatch(AuthAction.SetIsSingleTenant, jsonBody.IsSingleTenant ?? false);
                authStore.dispatch(AuthAction.SetSupportedAuthenticationTypes, supportedAuthenticationTypes);
                authStore.dispatch(
                    AuthAction.SetAuthenticationType,
                    window.localStorage.AuthenticationType ?? supportedAuthenticationTypes.first()
                );
                authStore.dispatch(AuthAction.SetAzureTenantId, jsonBody.AzureTenantId);
                authStore.dispatch(AuthAction.SetAzureAppId, jsonBody.AzureAppId);
                const self = this;
                authStore.dispatch(AuthAction.UserInfo);
                const subscription = this.$store.subscribe((mutation, _state) => {
                    if (mutation.type === authStore.getActionName(AuthAction.UserInfo)) {
                        subscription();
                        self.user = mutation.payload;
                        (window as any).user = self.user;
                        if (
                            !_state.AUTH.isLoggedIn &&
                            self.$route.name !== 'PasswordRecovery' &&
                            self.$route.name !== 'SignIn' &&
                            self.$route.name !== 'SignUp'
                        ) {
                            this.loading = false;
                            this.attachScrollEvent();
                            self.$router.push({ name: 'SignIn' });
                        } else {
                            this.loadUserData();
                            this.connectSignalR();
                        }
                    }
                });
            });
        });
        (window as any).Journaly.subscribe((tenantId: string): Promise<string> => {
            tenantsStore.dispatch(CrudAction.Get, { id: tenantId });
            return new Promise((resolve) => resolve('opened'));
        }, EventTypeEnum.TenantChanged);
        (window as any).Journaly.subscribe((): Promise<string> => {
            this.loadUserData();
            return new Promise((resolve) => resolve('loadingUserData'));
        }, EventTypeEnum.SignedIn);
        (window as any).Journaly.subscribe((): Promise<string> => {
            this.quickLoginDialog = true;
            return new Promise((resolve) => resolve('requestingQuickLogin'));
        }, EventTypeEnum.QuickLoginRequested);
        this.$store.subscribe((mutation, _state) => {
            if (mutation.type === tenantsStore.getActionName(CrudResponseAction.GetSuccess)) {
                const tenantId = authStore.useGetter(AuthGetter.GetTenant);
                if (tenantId && mutation.payload.item?.id) {
                    const tenant = mutation.payload.item as TenantModel;
                    if (tenant.isQuickLoginEnabled && tenant.quickLoginTimeout && tenant.quickLoginTimeout > 0) {
                        this.quickLoginTimeout = tenant.quickLoginTimeout!;
                        if (window.localStorage.QuickLoginLocked == 'true') {
                            this.quickLoginPassTime = new Date().getTime();
                            this.quickLoginDialog = true;
                            this.isQuickLoginLocked = true;
                        } else {
                            this.resetTimer();
                        }
                    } else {
                        this.quickLoginTimeout = 0;
                        window.localStorage.QuickLoginLocked = null;
                        this.resetTimer();
                    }
                }
            }
        });
        this.listenToAnyInteraction();
    }

    destroyed() {
        this.detachListenToAnyInteraction();
    }

    resetTimer() {
        const quickLoginTimeout = this.quickLoginTimeout ?? 0;
        if (quickLoginTimeout > 0) {
            const newTime = new Date().getTime() + 60 * quickLoginTimeout * 1000;
            if (newTime >= this.quickLoginPassTime + 1000) {
                this.quickLoginPassTime = newTime;
            }
        } else {
            this.quickLoginPassTime = 0;
        }
    }

    listenToAnyInteraction() {
        const events = ['click', 'keydown', 'mousemove', 'touchstart', 'scroll'];
        events.forEach((event) => {
            window.addEventListener(event, this.resetTimer, { passive: true });
        });
        this.refreshInterval = setInterval(() => {
            if (this.isSharedMode) {
                this.quickLoginRemainingSeconds =
                    this.quickLoginPassTime > 0 ? Math.ceil((this.quickLoginPassTime - new Date().getTime()) / 1000) : 0;
                if (
                    this.quickLoginPassTime > 0 &&
                    this.quickLoginRemainingSeconds <= 0 &&
                    !this.isQuickLoginLocked &&
                    this.isSharedMode
                ) {
                    this.quickLoginDialog = true;
                    this.isQuickLoginLocked = true;
                    window.localStorage.QuickLoginLocked = 'true';
                }
            }
        }, 1000);
    }

    detachListenToAnyInteraction() {
        const events = ['click', 'keydown', 'mousemove', 'touchstart', 'scroll'];
        events.forEach((event) => {
            window.removeEventListener(event, this.resetTimer);
        });
        clearInterval(this.refreshInterval);
    }

    attachScrollEvent() {
        this.$nextTick(() => {
            const layoutContent = document.querySelector('.layout-content');
            if (layoutContent) {
                layoutContent.addEventListener('scroll', () => {
                    if (layoutContent.scrollTop > 80) {
                        layoutContent.classList.add('scrolled');
                    } else {
                        layoutContent.classList.remove('scrolled');
                    }
                    if (this.previousScrollTop == -1) {
                        this.previousScrollTop = layoutContent.scrollTop;
                    }
                    if (Math.abs(this.previousScrollTop - layoutContent.scrollTop) > 80) {
                        this.previousScrollTop = layoutContent.scrollTop;
                        (window as any).Journaly.publish(EventTypeEnum.BigScroll);
                    }
                });
            }
        });
    }

    toggleClick() {
        (this.$refs.dockSidebar as any).toggle();
    }

    connectSignalR() {
        NotificationService.init();
    }

    signout(ask = false) {
        if (ask) {
            this.signoutDialog = true;
        } else {
            this.quickLoginDialog = false;
            this.isQuickLoginLocked = false;
            this.signoutDialog = false;
            this.deactivateQuickLoginDialog = false;
            window.localStorage.TabletMode = null;
            window.localStorage.SharedMode = null;
            window.localStorage.QuickLoginLocked = null;
            this.onDeactivatedQuickLogin();
            authStore.dispatch(AuthAction.SetSharedMode, false);
            authStore.dispatch(AuthAction.SignOut);
            const authenticationType = authStore.useGetter(AuthGetter.GetAuthenticationType) as AuthenticationType;
            if (authenticationType == AuthenticationType.AzureActiveDirectory) {
                const azureTenantId = authStore.useGetter(AuthGetter.GetAzureTenantId);
                const azureAppId = authStore.useGetter(AuthGetter.GetAzureAppId);
                const msalConfig = {
                    auth: {
                        clientId: azureAppId,
                        authority: 'https://login.microsoftonline.com/' + azureTenantId,
                    },
                };
                const msalInstance = new PublicClientApplication(msalConfig);
                msalInstance.handleRedirectPromise();
                msalInstance.logoutPopup();
            }
        }
    }

    loadUserData() {
        applicationUserStore.dispatch(CrudAction.Get, {});
        this.subscribe(applicationUserStore, CrudReponse.Get)
            .then((e: any) => {
                console.log((e.language = e.language ?? 'cs'));
                const language = e.language ?? 'cs';
                this.$i18n.locale = language;
                window.localStorage.language = language;
                this.$vuetify.lang.current = language;
                GlobalVariables.lang = language;
                moment.locale(this.$vuetify.lang.current);
                tenantsStore.dispatch(
                    CrudAction.GetAll,
                    createCrudQueryPayload<TenantModel>({
                        field: 'name',
                        order: 'asc',
                    })
                );
                this.subscribe(tenantsStore, CrudReponse.GetAll).then((e: TenantModel[]) => {
                    this.detectSystemUser();
                    if (e.length > 0) {
                        this.loadCodebooks().then(() => {
                            let tenantId = this.$route.params.tenantId;
                            this.selectedTenant = tenantId;
                            if (window.localStorage.Locked == 'true') {
                                authStore.dispatch(AuthAction.SetIsLocked, true);
                                if (this.$router.currentRoute.name != 'PanelDashboard') {
                                    this.isLocked = true;
                                    this.deactivateQuickLoginDialog = true;
                                    this.isQuickLoginLocked = true;
                                }
                            }
                            if (
                                !tenantId &&
                                window.localStorage.CurrentTenant &&
                                e.any((x) => x.id == window.localStorage.CurrentTenant)
                            ) {
                                tenantId = window.localStorage.CurrentTenant;
                                authStore.dispatch(AuthAction.SetTenant, tenantId);
                                authStore.dispatch(AuthAction.SetSharedMode, false);

                                const tenant = e.first((x) => x.id == window.localStorage.CurrentTenant);
                                if (tenant.isQuickLoginEnabled) {
                                    const isSharedMode =
                                        window.localStorage.TabletMode == 'true' || window.localStorage.SharedMode == 'true';
                                    authStore.dispatch(AuthAction.SetSharedMode, isSharedMode);
                                } else {
                                    window.localStorage.TabletMode = null;
                                    window.localStorage.SharedMode = 'false';
                                }
                                const isNotificationMode = window.localStorage.NotificationMode == 'true';
                                authStore.dispatch(AuthAction.SetNotificationMode, isNotificationMode);

                                this.loadTenantStores();
                                this.selectedTenant = tenantId;
                            } else if (!tenantId && e.length > 0) {
                                authStore.dispatch(AuthAction.SetTenant, e[0].id!);
                                authStore.dispatch(AuthAction.SetSharedMode, false);
                                window.localStorage.TabletMode = null;
                                window.localStorage.SharedMode = 'false';
                                this.loadTenantStores(true);
                                this.selectedTenant = e[0].id!;
                            } else {
                                authStore.dispatch(AuthAction.SetTenant, tenantId);
                                const tenant = e.first((x) => x.id == window.localStorage.CurrentTenant);
                                if (tenant.isQuickLoginEnabled) {
                                    const isSharedMode =
                                        window.localStorage.TabletMode == 'true' || window.localStorage.SharedMode == 'true';
                                    authStore.dispatch(AuthAction.SetSharedMode, isSharedMode);
                                } else {
                                    window.localStorage.TabletMode = null;
                                    window.localStorage.SharedMode = 'false';
                                }
                                const isNotificationMode = window.localStorage.NotificationMode == 'true';
                                authStore.dispatch(AuthAction.SetNotificationMode, isNotificationMode);
                                this.loadTenantStores();
                                this.selectedTenant = tenantId;
                            }
                            if (this.getQuery('take')) {
                                this.takeItemId = this.getQuery('take');
                                this.setQuery('take', null);
                            }
                        });
                    } else {
                        authStore.dispatch(AuthAction.SetTenant, '');
                        this.$router.push({ name: 'UserSettings' });
                        this.loading = false;
                        this.attachScrollEvent();
                    }
                });
            })
            .catch((e: any) => {
                this.loading = false;
                this.attachScrollEvent();
            });
    }

    loadCodebooks(): Promise<any> {
        return new Promise((resolve, reject) => {
            codebooksStore.dispatch(CrudAction.GetAll, createCrudQueryPayload<CodebookModel>());
            this.subscribe(codebooksStore, CrudReponse.GetAll)
                .then((e: CodebookModel[]) => {
                    resolve(e);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    loadTenantStores(enableRouteChanged = false) {
        this.loading = true;
        this.loadRoles().then(() => {
            this.loadTenantUser().then(() => {
                this.detectPermissions();
                this.detectLicence();
                this.detectTwoFactorRequirements();
                this.loadTenantUsers().then(() => {
                    Promise.all([
                        this.loadLocations(),
                        this.loadWorkplaces(),
                        this.loadStations(),
                        this.loadShiftTypes(),
                        this.loadGroups(),
                        this.loadCustomers(),
                        this.loadScrapReasons(),
                        this.loadScrapReasonWorkplaces(),
                        this.loadProducts(),
                        this.loadProjects(),
                    ]).then(() => {
                        this.$nextTick(() => {
                            this.loading = false;
                            this.attachScrollEvent();
                            if (enableRouteChanged) {
                                const route = this.getFirstRoute(this.selectedTenant);
                                if (route) {
                                    this.$router.push(route);
                                } else {
                                    this.$router.push({ name: 'UserSettings' });
                                }
                            }
                        });
                    });
                });
            });
        });
    }

    loadTenantUser(): Promise<any> {
        return new Promise((resolve, reject) => {
            const user = applicationUserStore.useGetter(CrudGetter.Detail) as ApplicationUserModel;
            tenantUsersStore.dispatch(
                CrudAction.GetAll,
                createCrudQueryPayload<TenantUserModel>(
                    undefined,
                    [
                        {
                            field: 'userId',
                            op: 'eq',
                            comparand: user.id!,
                        },
                    ],
                    undefined,
                    false,
                    'me'
                )
            );
            this.subscribe(tenantUsersStore, CrudReponse.GetAll, 'me')
                .then((e: TenantUserModel[]) => {
                    authStore.dispatch(AuthAction.SetTenantUser, e[0]);
                    resolve(e);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    loadTenantUsers(): Promise<any> {
        return new Promise((resolve, reject) => {
            tenantUsersStore.dispatch(CrudAction.GetAll, createCrudQueryPayload<TenantUserModel>());
            this.subscribe(tenantUsersStore, CrudReponse.GetAll)
                .then((e: TenantUserModel[]) => {
                    resolve(e);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    loadLocations(): Promise<any> {
        return new Promise((resolve, reject) => {
            locationsStore.dispatch(CrudAction.GetAll, createCrudQueryPayload<WorkplaceModel>());
            this.subscribe(locationsStore, CrudReponse.GetAll)
                .then((e: LocationModel[]) => {
                    resolve(e);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    loadWorkplaces(): Promise<any> {
        return new Promise((resolve, reject) => {
            workplacesStore.dispatch(CrudAction.GetAll, createCrudQueryPayload<WorkplaceModel>());
            this.subscribe(workplacesStore, CrudReponse.GetAll)
                .then((e: WorkplaceModel[]) => {
                    resolve(e);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    loadStations(): Promise<any> {
        return new Promise((resolve, reject) => {
            stationsStore.dispatch(CrudAction.GetAll, createCrudQueryPayload<StationModel>());
            this.subscribe(stationsStore, CrudReponse.GetAll)
                .then((e: StationModel[]) => {
                    resolve(e);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    loadScrapReasons(): Promise<any> {
        return new Promise((resolve, reject) => {
            scrapReasonsStore.dispatch(CrudAction.GetAll, createCrudQueryPayload<ScrapReasonModel>());
            this.subscribe(scrapReasonsStore, CrudReponse.GetAll)
                .then((e: ScrapReasonModel[]) => {
                    resolve(e);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    loadScrapReasonWorkplaces(): Promise<any> {
        return new Promise((resolve, reject) => {
            scrapReasonWorkplacesStore.dispatch(CrudAction.GetAll, createCrudQueryPayload<ScrapReasonWorkplaceModel>());
            this.subscribe(scrapReasonWorkplacesStore, CrudReponse.GetAll)
                .then((e: ScrapReasonModel[]) => {
                    resolve(e);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    loadCustomers(): Promise<CustomerModel[]> {
        return new Promise((resolve, reject) => {
            customersStore.dispatch(CrudAction.GetAll, createCrudQueryPayload<CustomerModel>());
            this.subscribe(customersStore, CrudReponse.GetAll)
                .then((e: CustomerModel[]) => {
                    resolve(e);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    loadProducts(): Promise<CustomerModel[]> {
        return new Promise((resolve, reject) => {
            productsStore.dispatch(CrudAction.GetAll, createCrudQueryPayload<ProductModel>());
            this.subscribe(productsStore, CrudReponse.GetAll)
                .then((e: ProductModel[]) => {
                    resolve(e);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    loadProjects(): Promise<ProjectModel[]> {
        return new Promise((resolve, reject) => {
            projectsStore.dispatch(CrudAction.GetAll, createCrudQueryPayload<ProjectModel>());
            this.subscribe(projectsStore, CrudReponse.GetAll)
                .then((e: ProductModel[]) => {
                    resolve(e);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    loadShiftTypes(): Promise<any> {
        return new Promise((resolve, reject) => {
            shiftTypesStore.dispatch(CrudAction.GetAll, createCrudQueryPayload<ShiftTypeModel>());
            this.subscribe(shiftTypesStore, CrudReponse.GetAll)
                .then((e: ShiftTypeModel[]) => {
                    resolve(e);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    loadRoles(): Promise<any> {
        return new Promise((resolve, reject) => {
            rolesStore.dispatch(CrudAction.GetAll, createCrudQueryPayload<RoleModel>());
            this.subscribe(rolesStore, CrudReponse.GetAll)
                .then((e: RoleModel[]) => {
                    resolve(e);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    loadGroups(): Promise<any> {
        return new Promise((resolve, reject) => {
            groupsStore.dispatch(CrudAction.GetAll, createCrudQueryPayload<GroupModel>());
            this.subscribe(groupsStore, CrudReponse.GetAll)
                .then((e: RoleModel[]) => {
                    resolve(e);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    formatDateTime(dateTime: string) {
        return formatDate(dateTime);
    }

    toUserSettings() {
        let tenantId = '';
        if (this.tenantId) tenantId = this.tenantId;
        else tenantId = '';

        if (tenantId != '') this.$router.push({ name: 'UserSettings', params: { tenantId: tenantId } });
        else this.$router.push({ name: 'UserSettings' });
    }

    getDataList(): Array<any> {
        const tenantId = authStore.useGetter(AuthGetter.GetTenant);
        const useTpm = (window as any).UseTpm;
        if (tenantId && tenantId.length > 0) {
            const onlyDashboard = authStore.useGetter(AuthGetter.GetOnlyDashboard);
            const onlyDashboardAndCalendar = authStore.useGetter(AuthGetter.GetOnlyDashboardAndCalendar);
            const disableDashboard = authStore.useGetter(AuthGetter.GetDisableDashboard);
            if (onlyDashboardAndCalendar) {
                return [
                    {
                        text: this.$i18n.t('menu.dashboard'),
                        to: { name: 'Dashboard', params: { tenantId: tenantId } },
                        icon: 'dashboard.svg',
                    },
                    {
                        text: this.$i18n.t('menu.tasks'),
                        to: { name: 'WorkTasks', params: { tenantId: tenantId } },
                        icon: 'calendar.svg',
                        permissions: [PermissionTypeEnum.Response, PermissionTypeEnum.PerformAudit, PermissionTypeEnum.Plan],
                    },
                ].filter((x) => this.hasPermissions(x.permissions ?? []) || !x.permissions || x.permissions.length == 0);
            } else if (onlyDashboard) {
                return [
                    {
                        text: this.$i18n.t('menu.dashboard'),
                        to: { name: 'Dashboard', params: { tenantId: tenantId } },
                        icon: 'dashboard.svg',
                    },
                ];
            }
            let items: {
                text: any;
                to: any;
                icon: any;
                permissions?: Array<PermissionTypeEnum>;
            }[] = !disableDashboard
                ? [
                      {
                          text: this.$i18n.t('menu.dashboard'),
                          to: { name: 'Dashboard', params: { tenantId: tenantId } },
                          icon: 'dashboard.svg',
                      },
                  ]
                : [];
            items = [
                ...items,
                ...[
                    {
                        text: this.$i18n.t('menu.audit'),
                        to: { name: 'Audits', params: { tenantId: tenantId } },
                        icon: 'audits.svg',
                        permissions: [
                            PermissionTypeEnum.Audits,
                            PermissionTypeEnum.EditAllAudits,
                            PermissionTypeEnum.EditOwnedAudit,
                            PermissionTypeEnum.PerformAudit,
                        ],
                    },
                ],
            ];
            if (useTpm) {
                items = [
                    ...items,
                    ...[
                        {
                            text: this.$i18n.t('menu.shiftWork'),
                            to: { name: 'ShiftWork', params: { tenantId: tenantId } },
                            icon: 'shiftWork.svg',
                            permissions: [PermissionTypeEnum.ShiftManagement],
                        },
                        {
                            text: this.$i18n.t('menu.shifts'),
                            to: { name: 'Shifts', params: { tenantId: tenantId } },
                            icon: 'shifts.svg',
                            permissions: [PermissionTypeEnum.ShiftManagement],
                        },
                        {
                            text: this.$i18n.t('menu.shiftStations'),
                            to: { name: 'ShiftStations', params: { tenantId: tenantId } },
                            icon: 'shiftStations.svg',
                            permissions: [PermissionTypeEnum.ShiftManagement],
                        },
                        {
                            text: this.$i18n.t('menu.shiftWorkList'),
                            to: { name: 'ShiftWorkList', params: { tenantId: tenantId } },
                            icon: 'shiftWorks.svg',
                            permissions: [PermissionTypeEnum.ShiftWork, PermissionTypeEnum.ShiftWorkLimited],
                        },
                        {
                            text: this.$i18n.t('menu.shiftReport'),
                            to: { name: 'ShiftReport', params: { tenantId: tenantId } },
                            icon: 'shiftReport.svg',
                            permissions: [PermissionTypeEnum.ShiftOverview],
                        },
                    ],
                ];
            }
            items = [
                ...items,
                ...[
                    {
                        text: this.$i18n.t('menu.tasks'),
                        to: { name: 'WorkTasks', params: { tenantId: tenantId } },
                        icon: 'calendar.svg',
                        permissions: [PermissionTypeEnum.Response, PermissionTypeEnum.PerformAudit, PermissionTypeEnum.Plan],
                    },
                ],
            ];
            if (useTpm) {
                items = [
                    ...items,
                    ...[
                        {
                            text: this.$i18n.t('menu.issuesSolving'),
                            to: { name: 'IssuesSolving', params: { tenantId: tenantId } },
                            icon: 'issuesSolving.svg',
                            permissions: [PermissionTypeEnum.ReportIssueSolving, PermissionTypeEnum.PerformAudit],
                        },
                    ],
                ];
            }
            items = [
                ...items,
                ...[
                    {
                        text: this.$i18n.t('menu.productInspection'),
                        to: { name: 'ProductInspections', params: { tenantId: tenantId } },
                        icon: 'productInspection.svg',
                        permissions: [PermissionTypeEnum.PerformProductInspection],
                    },
                    {
                        text: this.$i18n.t('menu.responses'),
                        to: { name: 'Responses', params: { tenantId: tenantId } },
                        icon: 'responses.svg',
                        permissions: [
                            PermissionTypeEnum.PerformAudit,
                            PermissionTypeEnum.Response,
                            PermissionTypeEnum.MistakeCorrection,
                        ],
                    },
                ],
            ];
            if (useTpm) {
                items = [
                    ...items,
                    ...[
                        {
                            text: this.$i18n.t('menu.reportedIssueSolving'),
                            to: { name: 'ReportedIssueSolving', params: { tenantId: tenantId } },
                            icon: 'reportedIssueSolving.svg',
                            permissions: [
                                PermissionTypeEnum.PerformAudit,
                                PermissionTypeEnum.Response,
                                PermissionTypeEnum.MistakeCorrection,
                            ],
                        },
                        {
                            text: this.$i18n.t('menu.reportedMaintenance'),
                            to: { name: 'ReportedMaintenance', params: { tenantId: tenantId } },
                            icon: 'reportedMaintenance.svg',
                            permissions: [
                                PermissionTypeEnum.PerformAudit,
                                PermissionTypeEnum.Response,
                                PermissionTypeEnum.MistakeCorrection,
                            ],
                        },
                        {
                            text: this.$i18n.t('menu.reportedChecklists'),
                            to: { name: 'ReportedChecklists', params: { tenantId: tenantId } },
                            icon: 'reportedChecklists.svg',
                            permissions: [
                                PermissionTypeEnum.PerformAudit,
                                PermissionTypeEnum.Response,
                                PermissionTypeEnum.MistakeCorrection,
                            ],
                        },
                    ],
                ];
            }
            items = [
                ...items,
                ...[
                    {
                        text: this.$i18n.t('menu.reportedProductInspection'),
                        to: { name: 'ReportedProductInspection', params: { tenantId: tenantId } },
                        icon: 'reportedProductInspection.svg',
                        permissions: [PermissionTypeEnum.ProductInspections, PermissionTypeEnum.MistakeCorrection],
                    },
                    {
                        text: this.$i18n.t('menu.unfinishedResponses'),
                        to: { name: 'Unanswered', params: { tenantId: tenantId } },
                        icon: 'unanswered-responses.svg',
                        permissions: [PermissionTypeEnum.PerformAudit, PermissionTypeEnum.Response],
                    },
                ],
            ];
            if (useTpm) {
                items = [
                    ...items,
                    ...[
                        {
                            text: this.$i18n.t('menu.waitingMaintenance'),
                            to: { name: 'WaitingMaintenance', params: { tenantId: tenantId } },
                            icon: 'waitingMaintenance.svg',
                            permissions: [PermissionTypeEnum.PerformAudit, PermissionTypeEnum.Response],
                        },
                    ],
                ];
            }
            items = [
                ...items,
                ...[
                    {
                        text: this.$i18n.t('menu.issues'),
                        to: { name: 'Issues', params: { tenantId: tenantId } },
                        icon: 'issues.svg',
                        permissions: [PermissionTypeEnum.Findings, PermissionTypeEnum.FindingManager],
                    },
                    {
                        text: this.$i18n.t('menu.findings'),
                        to: { name: 'Findings', params: { tenantId: tenantId } },
                        icon: 'findings.svg',
                        permissions: [PermissionTypeEnum.Findings, PermissionTypeEnum.FindingManager],
                    },
                    {
                        text: this.$i18n.t('menu.solvedIssues'),
                        to: { name: 'Measures', params: { tenantId: tenantId } },
                        icon: 'measure.svg',
                        permissions: [
                            PermissionTypeEnum.Measures,
                            PermissionTypeEnum.MeasuresAllUsers,
                            PermissionTypeEnum.MeasureView,
                        ],
                    },
                    {
                        text: this.$i18n.t('menu.verification'),
                        to: { name: 'Verification', params: { tenantId: tenantId } },
                        icon: 'resources.svg',
                        permissions: [PermissionTypeEnum.MeasureVerification, PermissionTypeEnum.MeasureView],
                    },
                    {
                        text: this.$i18n.t('menu.review'),
                        to: { name: 'Review', params: { tenantId: tenantId } },
                        icon: 'tasks.svg',
                        permissions: [PermissionTypeEnum.PlanItemReview],
                    },
                ],
            ];
            if (useTpm) {
                items = [
                    ...items,
                    ...[
                        {
                            text: this.$i18n.t('menu.panelDashboards'),
                            to: { name: 'PanelDashboards', params: { tenantId: tenantId } },
                            icon: 'panelDashboards.svg',
                            permissions: [PermissionTypeEnum.PanelDashboardEdit, PermissionTypeEnum.PanelDashboardView],
                        },
                    ],
                ];
            }
            items = [
                ...items,
                ...[
                    {
                        text: this.$i18n.t('menu.reports'),
                        to: { name: 'ReportByQuestion', params: { tenantId: tenantId } },
                        icon: 'reports.svg',
                        permissions: [
                            PermissionTypeEnum.Response,
                            PermissionTypeEnum.Audits,
                            PermissionTypeEnum.PerformProductInspection,
                        ],
                    },
                ],
            ];
            return items.filter((x) => this.hasPermissions(x.permissions ?? []) || !x.permissions || x.permissions.length == 0);
        } else {
            return [];
        }
    }

    getFirstRoute(tenantId: string | null): any {
        const routes = this.getDataList();
        for (const route of routes) {
            if (this.hasPermissions(route.permissions)) {
                return { name: route.to.name, params: { tenantId } };
            }
        }
        return null;
    }

    getPageTitle() {
        this.pageTitle = this.$router.currentRoute?.meta?.title ?? '';
    }

    isMobileHeaderShown() {
        if (this.$router.currentRoute?.meta?.hideMobileHeader)
            this.showMobileHeader = !this.$router.currentRoute.meta?.hideMobileHeader;
        else this.showMobileHeader = true;
    }

    getStringTranslation(string: string | null | undefined) {
        if (string) return this.$i18n.t(string);
        else return null;
    }

    setDocumentTitle() {
        if (this.$router.currentRoute.meta?.title) {
            const title = this.getStringTranslation(this.$router.currentRoute.meta?.title) as string;
            document.title = title + ' - Audit Management Software';
        } else {
            document.title = 'Audit Management Software';
        }
    }

    private detectTenantMenu() {
        this.tenantMenu = this.$router.currentRoute.meta?.tenant != false;
    }

    private detectPermissions() {
        const disableDashboard = authStore.useGetter(AuthGetter.GetDisableDashboard);
        if (
            this.$router.currentRoute.meta?.permissions ||
            (this.$router.currentRoute.name == 'Dashboard' && disableDashboard)
        ) {
            const neededPermissions = (this.$router.currentRoute.meta?.permissions ?? []) as PermissionTypeEnum[];
            const userPermissions = authStore.useGetter(AuthGetter.GetPermissions) as PermissionTypeEnum[];
            if (
                !userPermissions.any((x) => x == PermissionTypeEnum.Admin) &&
                !neededPermissions.any((x) => userPermissions.contains(x))
            ) {
                if (
                    userPermissions.contains(PermissionTypeEnum.ShiftWork) ||
                    userPermissions.contains(PermissionTypeEnum.ShiftWorkLimited)
                ) {
                    const firstRoute = this.getDataList().shift();
                    if (firstRoute) {
                        if (firstRoute.to.name != 'Dashboard') {
                            this.$router.replace(firstRoute.to);
                            return;
                        }
                    }
                }
                this.hasAccessToRoute = false;
                //this.$router.push({ name: 'Home', params: { tenantId: this.tenantId! } });
            } else {
                this.hasAccessToRoute = true;
            }
        } else {
            this.hasAccessToRoute = true;
        }
    }

    private detectSystemUser() {
        if (this.$router.currentRoute.meta?.systemUser == true) {
            const applicationUser = applicationUserStore.useGetter(CrudGetter.Detail) as ApplicationUserModel;
            if (!applicationUser?.systemUser) {
                this.$router.push({ name: 'Home', params: { tenantId: this.tenantId! } });
            }
        }
    }

    private detectLicence() {
        if (this.$router.currentRoute.meta?.checkLicence) {
            const tenant = tenantsStore.useGetter(CrudGetter.Detail) as TenantModel;
            if (tenant) {
                const licenceToDate = tenant.licenceToDate ? moment(tenant.licenceToDate) : moment().add(-1, 'days');
                if (licenceToDate.isBefore(moment())) {
                    this.$router.push({ name: 'BadLicence', params: { tenantId: this.tenantId! } });
                    return;
                }
            }
            const tenantUser = authStore.useGetter(AuthGetter.GetTenantUser) as TenantUserModel;
            if (tenantUser) {
                if (!tenantUser.isActive) {
                    this.$router.push({ name: 'BadLicence', params: { tenantId: this.tenantId! } });
                    return;
                }
            }
            if (!this.hasAnyPermissions()) {
                this.$router.push({ name: 'BadLicence', params: { tenantId: this.tenantId! } });
                return;
            }
        }
    }

    private detectTwoFactorRequirements() {
        if (this.$router.currentRoute.meta?.checkLicence) {
            const tenant = tenantsStore.useGetter(CrudGetter.Detail) as TenantModel;
            const user = applicationUserStore.useGetter(CrudGetter.Detail) as ApplicationUserModel;
            if (
                tenant &&
                user &&
                tenant.isRequiredTwoFactor &&
                !user.twoFactorEnabled &&
                authStore.useGetter(AuthGetter.GetAuthenticationType) == AuthenticationType.Standard
            ) {
                this.$router.push({ name: 'MissingMfa', params: { tenantId: this.tenantId! } });
                return;
            }
        }
    }

    protected onQuickLoginClick() {
        this.quickLoginDialog = true;
    }

    protected onQuickLoginLogout() {
        this.quickLoginDialog = true;
        this.isQuickLoginLocked = true;
        window.localStorage.QuickLoginLocked = 'true';
    }

    protected onNotificationUnsubscribe() {
        this.notificationSubscription.onUnsubscribeClick();
    }

    protected onActivateQuickLoginClick() {
        this.activateQuickLoginDialog = true;
    }

    protected onDeactivateQuickLoginClick() {
        this.deactivateQuickLoginDialog = true;
    }

    protected onDeactivatedQuickLogin() {
        this.quickLoginDialog = false;
        this.isQuickLoginLocked = false;
        this.isLocked = false;
        authStore.dispatch(AuthAction.SetIsLocked, false);
    }
}
